import { useEffect, useRef, useState } from 'react';
import { useT } from '../../i18n/I18nProvider';
import { useGuides } from './GuideProvider';
import { GUIDES } from './registry';

/** A question mark in a circle. */
function HelpIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3" />
      <path d="M12 17h.01" />
    </svg>
  );
}

/** The help button on a module's pages: take the tour again, or read the guide. */
export function HelpButton({ guide }: { guide: string }) {
  const t = useT();
  const { startTour, openGuide } = useGuides();
  const [open, setOpen] = useState(false);
  // The menu hangs to the left of the button, unless the button sits in the left half of the screen, where it would run under the sidebar.
  const [fromStart, setFromStart] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [open]);

  return (
    <div className="rr-help" ref={ref} data-guide={`${guide}-help`}>
      <button type="button" className="rr-help__button" aria-label={t('guide.help.label')} aria-haspopup="menu" aria-expanded={open}
        title={t('guide.help.label')} onClick={() => { setFromStart((ref.current?.getBoundingClientRect().left ?? window.innerWidth) < window.innerWidth / 2); setOpen((o) => !o); }} data-testid="help-button">
        <HelpIcon />
      </button>
      {open && (
        <div className={`rr-help__menu${fromStart ? ' rr-help__menu--start' : ''}`} role="menu">
          <button type="button" role="menuitem" onClick={() => { setOpen(false); startTour(guide); }}>
            <strong>{t('guide.help.tour')}</strong><span>{t('guide.help.tourHint')}</span>
          </button>
          {(GUIDES[guide]?.howTos ?? []).map((h) => (
            <button key={h.id} type="button" role="menuitem" onClick={() => { setOpen(false); startTour(guide, h.id); }} data-testid={`howto-${h.id}`}>
              <strong>{t(h.titleKey)}</strong><span>{t(h.hintKey)}</span>
            </button>
          ))}
          <button type="button" role="menuitem" onClick={() => { setOpen(false); openGuide(guide); }}>
            <strong>{t('guide.help.read')}</strong><span>{t('guide.help.readHint')}</span>
          </button>
        </div>
      )}
    </div>
  );
}
